import { useState } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, ChecklistMenuField, DropdownField, Text } from '@frontend/design-system';
import { GetRuleExceptionFormFieldProps } from '../hooks/use-create-edit-rule-exception-form';
import { ResourceDropdownOption } from './exception-target-form';

type ExceptionResourcesFormProps = {
  getFieldProps: GetRuleExceptionFormFieldProps;
  providerList: ResourceDropdownOption[];
  operatoryList: ResourceDropdownOption[];
  serviceList: ResourceDropdownOption[];
};

export const ExceptionResourcesForm = ({
  getFieldProps,
  providerList,
  operatoryList,
  serviceList,
}: ExceptionResourcesFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const target = getFieldProps('target').value;
  const combinationProviderFieldProps = getFieldProps('provider');
  const combinationOperatoryFieldProps = getFieldProps('operatory');
  const combinationServiceFieldProps = getFieldProps('service');
  const [showProviderField, setShowProviderField] = useState(Boolean(combinationProviderFieldProps.value));
  const [showOperatoryField, setShowOperatoryField] = useState(Boolean(combinationOperatoryFieldProps.value));
  const [showServiceField, setShowServiceField] = useState(Boolean(combinationServiceFieldProps.value));
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();

  if (target === 'RESOURCE_COMBINATION') {
    return (
      <div css={resourcesFormContainerStyles}>
        <Text color='subdued' css={{ fontStyle: 'italic' }}>
          {t('The exception applies only when all selected criteria match.')}
        </Text>
        {showProviderField ? (
          <FieldWrapper iconName='provider' title={t('Provider')}>
            <DropdownField label='' aria-label={t('Select provider')} {...combinationProviderFieldProps}>
              <DropdownField.Option value=''>{t('Select provider')}</DropdownField.Option>
              {providerList.map(({ label, value }) => (
                <DropdownField.Option key={value} value={value}>
                  {label}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FieldWrapper>
        ) : (
          <AddButton label={t('+ Provider')} onClick={() => setShowProviderField(true)} />
        )}

        {isV3OperatorySupported &&
          (showOperatoryField ? (
            <FieldWrapper iconName='operatory' title={t('Operatory')}>
              <DropdownField label='' aria-label={t('Select operatory')} {...combinationOperatoryFieldProps}>
                <DropdownField.Option value=''>{t('Select operatory')}</DropdownField.Option>
                {operatoryList.map(({ label, value }) => (
                  <DropdownField.Option key={value} value={value}>
                    {label}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </FieldWrapper>
          ) : (
            <AddButton label={t('+ Operatory')} onClick={() => setShowOperatoryField(true)} />
          ))}

        {showServiceField ? (
          <FieldWrapper iconName='appointment-type' title={t('Service')}>
            <DropdownField label='' aria-label={t('Select service')} {...combinationServiceFieldProps}>
              <DropdownField.Option value=''>{t('Select service')}</DropdownField.Option>
              {serviceList.map(({ label, value }) => (
                <DropdownField.Option key={value} value={value}>
                  {label}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FieldWrapper>
        ) : (
          <AddButton label={t('+ Service')} onClick={() => setShowServiceField(true)} />
        )}
      </div>
    );
  }

  return (
    <div css={resourcesFormContainerStyles}>
      <Text color='subdued' css={{ fontStyle: 'italic' }}>
        {t('The exception applies when at least one of the selected criteria match.')}
      </Text>
      <FieldWrapper iconName='provider' title={t('Providers')}>
        <ChecklistMenuField
          {...getFieldProps('providers')}
          label=''
          aria-label={t('Select providers')}
          placeholder={t('Select providers')}
          showSearchBar
          customRender={(props) => (
            <ChecklistMenuField.ChipInput
              {...props}
              variant='primary'
              onRemoveClick={(id) => {
                getFieldProps('providers').onChange?.({
                  name: 'providers',
                  value: [id],
                });
              }}
            />
          )}
        >
          {providerList.map((p) => (
            <ChecklistMenuField.Option key={p.value} value={p.value}>
              {p.label}
            </ChecklistMenuField.Option>
          ))}
        </ChecklistMenuField>
      </FieldWrapper>
      {isV3OperatorySupported && (
        <FieldWrapper iconName='operatory' title={t('Operatories')}>
          <ChecklistMenuField
            {...getFieldProps('operatories')}
            label=''
            aria-label={t('Select operatories')}
            placeholder={t('Select operatories')}
            showSearchBar
            maxAllowed={operatoryList.length}
            customRender={(props) => (
              <ChecklistMenuField.ChipInput
                {...props}
                variant='primary'
                onRemoveClick={(id) => {
                  getFieldProps('operatories').onChange?.({
                    name: 'operatories',
                    value: [id],
                  });
                }}
              />
            )}
          >
            {operatoryList.map((o) => (
              <ChecklistMenuField.Option key={o.value} value={o.value}>
                {o.label}
              </ChecklistMenuField.Option>
            ))}
          </ChecklistMenuField>
        </FieldWrapper>
      )}
      <FieldWrapper iconName='appointment-type' title={t('Services')}>
        <ChecklistMenuField
          {...getFieldProps('services')}
          label=''
          aria-label={t('Select service types')}
          placeholder={t('Select service types')}
          showSearchBar
          maxAllowed={serviceList.length}
          customRender={(props) => (
            <ChecklistMenuField.ChipInput
              {...props}
              variant='primary'
              onRemoveClick={(id) => {
                getFieldProps('services').onChange?.({
                  name: 'services',
                  value: [id],
                });
              }}
            />
          )}
        >
          {serviceList.map((s) => (
            <ChecklistMenuField.Option key={s.value} value={s.value}>
              {s.label}
            </ChecklistMenuField.Option>
          ))}
        </ChecklistMenuField>
      </FieldWrapper>
    </div>
  );
};

const resourcesFormContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.borderRadius.medium,
  marginTop: theme.spacing(2),
  backgroundColor: theme.colorsV2.blue10(),
});

const FieldWrapper = ({
  iconName,
  title,
  children,
}: {
  iconName: IconName;
  title: string;
  children: React.ReactNode;
}) => (
  <div css={fieldWrapperStyles}>
    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
      <Icon name={iconName} />
      <Text weight='bold'>{title}</Text>
    </div>
    <div css={{ backgroundColor: theme.colors.white }}>{children}</div>
  </div>
);

const fieldWrapperStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  borderRadius: theme.borderRadius.medium,
  backgroundColor: theme.colorsV2.blue10(),
});

const AddButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button variant='tertiary' css={addButtonStyles} onClick={onClick}>
    {label}
  </Button>
);

const addButtonStyles = css({
  color: theme.colors.primary[50],
  fontWeight: theme.font.weight.bold,
  width: 'fit-content',
});
